import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Modal, StatusTag, type StatusConfig } from '@/components/ui';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { fetchAccount } from '@/api/accounts';
import { CopyableId } from '@/pages/Console/Audit/CopyableId';
import { isPendingFirstLogin, type KeycloakAccountRow } from '../types';
import cells from '../accountsCells.module.css';

interface Props {
    row: KeycloakAccountRow | null;
    onClose: () => void;
    onReset: (row: KeycloakAccountRow) => void;
    onDelete: (row: KeycloakAccountRow) => void;
    onLock?: (row: KeycloakAccountRow) => void;
    canReset: boolean;
    canDelete: boolean;
    canLock?: boolean;
    canViewAudit: boolean;
}

/**
 * EAA-1820 (frame 32, honest subset) — the realm account drill-in. WHAT IS
 * DELIBERATELY NOT SHOWN, preserved verbatim across every revision of this file
 * because it is the most valuable thing in it: frame 32 draws "Tạo lúc … bởi …"
 * and a "Phiên đã đóng khi reset" line. Both are DROPPED rather than
 * fabricated — portal-api records no actor attribution on account creation and
 * no session-close event, so there is nothing behind either. "Đăng nhập gần
 * nhất" is the one field kept as an explicit unavailable state instead of
 * dropped, because the ticket brief distinguishes "no data source" (say so) from
 * "unsafe to build" (drop it); see `AccountsListTable.tsx` for the same
 * three-way distinction in the list column.
 *
 * EAA-1820 (frame 67): this panel used to render ONLY what the list row already
 * carried, and printed a note saying đơn vị / cấp bậc / per-app quyền had no
 * endpoint. `GET /accounts/<sub>` exists now, so it fetches them — the identity
 * fields keep coming from the row so the panel opens populated instead of blank
 * while that resolves.
 *
 * EAA-1839 (2026-08-21, user request) — REVIVED AND RESHAPED. This component was
 * dead code: `AccountsPage` rendered nothing and the account drill-in was the
 * route `/console/accounts/:sub` alone (see that file's docstring for the frame-72
 * decision that made it a route, which still stands for the "Xem" button). The
 * user asked for a row click to open a quick detail over the list instead of
 * leaving it, so this file is revived rather than a third detail surface written.
 *
 * It is a kit `Modal`, not the `Drawer` it was named for — hence the rename from
 * `AccountDetailDrawer`, matching the org screen's own
 * `ScopedAccountProfileDrawer` -> `ScopedAccountProfileModal` rename this ticket.
 * A component called `...Drawer` that renders a modal is exactly the stale naming
 * the codebase conventions exist to prevent.
 *
 * ONE SCROLL REGION: the modal BODY scrolls (`.acc-detail-modal .ant-modal-body`,
 * capped at `72vh` — viewport-relative, never px/rem, same value and same reason
 * as `.sa-profile`), the title and the action row do not, and the page behind
 * never gains a scrollbar.
 */
export function AccountDetailModal({
    row,
    onClose,
    onReset,
    onDelete,
    onLock,
    canReset,
    canDelete,
    canLock = false,
    canViewAudit,
}: Props) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();

    const STATUS_CONFIG: Record<string, StatusConfig> = {
        enabled: { label: t('console.accounts.detailDrawer.statusEnabled'), tone: 'success' },
        disabled: { label: t('console.accounts.detailDrawer.statusDisabled'), tone: 'error' },
    };

    /**
     * EAA-1820 (frame 67): this drawer used to render ONLY what the list row
     * already carried, and printed a note saying đơn vị / cấp bậc / per-app
     * quyền had no endpoint. `GET /accounts/<sub>` exists now, so it fetches
     * them — the identity fields keep coming from the row so the drawer opens
     * populated instead of blank while this resolves.
     */
    const detail = useQuery({
        queryKey: ['account-detail', row?.sub, accessToken],
        queryFn: () => fetchAccount(config.apiBase, accessToken as string, row?.sub as string),
        enabled: Boolean(row?.sub) && Boolean(accessToken),
        retry: false,
    });

    return (
        // EAA-1804: root debugging handle — every drawer/modal root needs a semantic
        // class per the className-naming convention (kit components merge it onto
        // their own root).
        // EAA-1839: `destroyOnClose` is deliberately absent — the query above is
        // keyed by `row.sub`, so re-opening the same row reads from cache instead of
        // refetching, and an unmount-per-close would throw that away.
        <Modal
            className="acc-detail-modal"
            open={Boolean(row)}
            onCancel={onClose}
            title={row?.username ?? ''}
            width={560}
            // The action row lives INSIDE the body (it always has), so the modal's
            // own footer would be a second, empty one.
            footer={null}
        >
            {row && (
                <div className="portal-accounts-detail">
                    <dl className="portal-accounts-detail-grid">
                        <dt>{t('console.accounts.detailDrawer.usernameLabel')}</dt>
                        <dd>
                            <code>{row.username}</code>
                        </dd>
                        <dt>{t('console.accounts.detailDrawer.emailLabel')}</dt>
                        <dd>{row.email || '—'}</dd>
                        <dt>{t('console.accounts.detailDrawer.subLabel')}</dt>
                        <dd>
                            <CopyableId value={row.sub} />
                        </dd>
                        <dt>{t('console.accounts.detailDrawer.statusLabel')}</dt>
                        <dd>
                            <StatusTag
                                status={row.enabled ? 'enabled' : 'disabled'}
                                config={STATUS_CONFIG}
                            />
                        </dd>
                        <dt>{t('console.accounts.detailDrawer.placementLabel')}</dt>
                        <dd>
                            {detail.isLoading && (
                                <span className="portal-admin-hint">{t('common.loading')}</span>
                            )}
                            {detail.data && detail.data.placements.length === 0 && (
                                <span className={cells.unassigned}>
                                    {t('console.accounts.detailDrawer.placementUnassigned')}
                                </span>
                            )}
                            {detail.data?.placements.map((p) => (
                                <span className="acc-detail-line" key={p.organization_id}>
                                    {p.organization_name}
                                    {p.level_name && ` · ${p.level_name}`}
                                </span>
                            ))}
                        </dd>
                        <dt>{t('console.accounts.detailDrawer.appAccessLabel')}</dt>
                        <dd>
                            {detail.isLoading && (
                                <span className="portal-admin-hint">{t('common.loading')}</span>
                            )}
                            {detail.data && detail.data.app_access.length === 0 && (
                                <span className={cells.unassigned}>
                                    {t('console.accounts.detailDrawer.noPermission')}
                                </span>
                            )}
                            {detail.data?.app_access.map((app) => (
                                <span className="acc-detail-line" key={app.application}>
                                    {app.application_name}:{' '}
                                    {app.roles.map((r) => (
                                        <code className={cells.approle} key={r}>
                                            {r}
                                        </code>
                                    ))}
                                </span>
                            ))}
                        </dd>
                        {isPendingFirstLogin(row) && (
                            <>
                                <dt>{t('console.accounts.detailDrawer.noteLabel')}</dt>
                                <dd>{t('console.accounts.detailDrawer.pendingFirstLoginNote')}</dd>
                            </>
                        )}
                    </dl>

                    {detail.isError && (
                        <p className="portal-admin-error">
                            {t('console.accounts.detailDrawer.fetchError')}
                        </p>
                    )}

                    {/* EAA-1804: kit `Button` instead of hand-written `<button className="portal-btn">`.
                        The kit's antd theme already carries this component sheet (controlHeight 36/34,
                        the cta palette, fontWeight 500 — see packages/ui/src/theme/antdTheme.ts), so
                        the mapping is props-for-classes: portal-btn -> default, -primary -> type="primary",
                        -danger -> danger. The audit entry stays a link, which antd renders as an <a>
                        when given `href`. */}
                    <div className="portal-accounts-detail-actions">
                        {canReset && (
                            <Button onClick={() => onReset(row)}>
                                {t('console.accounts.detailDrawer.resetButton')}
                            </Button>
                        )}
                        {canLock && row.enabled && onLock && (
                            <Button onClick={() => onLock(row)}>
                                {t('console.accounts.detailDrawer.lockButton')}
                            </Button>
                        )}
                        {canViewAudit && (
                            // EAA-1820: not a deep link — AuditPage/AccountAuditPanel keep their
                            // filters in local useState, not the URL, so there is no `?username=`
                            // to target yet. Links to the tab; the operator re-types the filter
                            // there. See the page-level report.
                            <Button href="/console/audit">
                                {t('console.accounts.detailDrawer.viewAuditLink')}
                            </Button>
                        )}
                        {canDelete && (
                            <Button danger onClick={() => onDelete(row)}>
                                {t('console.accounts.detailDrawer.deleteButton')}
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </Modal>
    );
}
